import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { judgesApi } from '../../api/facades';

const JUDGES_BASE = '/dashboard/settings/judges';

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm text-slate-700 dark:text-slate-200 focus:border-primary-300 focus:outline-none';

const JudgeCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [judge_name, setJudgeName] = useState('');
  const [is_active, setIsActive] = useState<'Yes' | 'No'>('Yes');

  const create = useMutation({
    mutationFn: () => judgesApi.create({ judge_name: judge_name.trim(), is_active }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['judges'] });
      const id = data?.data?.id ?? (data?.data && (data.data as any).id);
      if (id) navigate(`${JUDGES_BASE}/${id}`);
      else navigate(JUDGES_BASE);
      toast.success('Judge created.');
    },
    onError: (e: any) => toast.error(e?.message || 'Create failed.'),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!judge_name.trim()) return;
    create.mutate();
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
      <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">Add a new judge. Name is required.</p>

      <form onSubmit={onSubmit} className="space-y-6">
        <section>
          <h2 className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wide mb-3">Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <FormField label="Name" hint="Required">
                <input className={inputClass} value={judge_name} onChange={(e) => setJudgeName(e.target.value)} placeholder="e.g. Hon. Jane Doe" required />
              </FormField>
            </div>
            <FormField label="Active">
              <select className={inputClass} value={is_active} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No')}>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </FormField>
          </div>
        </section>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="secondary" type="button" onClick={() => navigate(JUDGES_BASE)}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!judge_name.trim() || create.isPending}>{create.isPending ? 'Creating…' : 'Create'}</Button>
        </div>
      </form>
    </div>
  );
};

export default JudgeCreatePage;
